import { useState, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Modal } from '@/components/Modal'
import { FormField } from '@/components/FormField'
import { Button } from '@/components/ui/button'
import { useToast } from '@/hooks/useToast'
import { useAuth } from '@/auth/useAuth'
import { useEmployees } from './queries'
import {
  useLeaveTypes, useBenefitPlans, useHolidayCalendars,
  addAttendance, createLeaveRequest, createShift, createHoliday,
  createEquityGrant, createBenefitEnrollment, createRetirementAccount, createHsaFsaAccount, createGarnishment,
} from './extras'

export function useInvalidate() {
  const qc = useQueryClient()
  const { user } = useAuth()
  return () => { if (user?.tenantId) qc.invalidateQueries({ queryKey: ['tenant', user.tenantId, 'payroll'] }) }
}

function EmployeeSelect({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const { data } = useEmployees({ status: 'active' })
  return (
    <FormField
      label="Employee"
      required
      control={
        <select value={value} onChange={(e) => onChange(e.target.value)}
          className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm">
          <option value="">Select…</option>
          {(data?.data ?? []).map((e) => (
            <option key={e.id} value={e.id}>{e.first_name} {e.last_name} ({e.employee_number})</option>
          ))}
        </select>
      }
    />
  )
}

export function CreateModal({
  open, onOpenChange, title, onSubmit, children, submitLabel = 'Save',
}: {
  open: boolean; onOpenChange: (v: boolean) => void; title: string
  onSubmit: () => Promise<void>; children: ReactNode; submitLabel?: string
}) {
  const [busy, setBusy] = useState(false)
  const { toast } = useToast()
  async function run() {
    setBusy(true)
    try {
      await onSubmit()
      onOpenChange(false)
    } catch (err) {
      toast({ title: 'Save failed', description: err instanceof Error ? err.message : String(err), variant: 'destructive' })
    } finally {
      setBusy(false)
    }
  }
  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      footer={
        <>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={run} disabled={busy}>{busy ? 'Saving…' : submitLabel}</Button>
        </>
      }
    >
      <div className="space-y-3">{children}</div>
    </Modal>
  )
}

const HOURS_KINDS = ['regular', 'overtime', 'double_time', 'pto', 'vacation', 'sick', 'holiday']

export function AddHoursModal({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const invalidate = useInvalidate()
  const { toast } = useToast()
  const [f, setF] = useState({ employee_id: '', entry_date: new Date().toISOString().slice(0, 10), hours: '', hours_kind: 'regular' })
  return (
    <CreateModal open={open} onOpenChange={onOpenChange} title="Log hours" onSubmit={async () => {
      if (!f.employee_id || !f.hours) { toast({ title: 'Employee and hours required', variant: 'destructive' }); throw new Error('validation') }
      await addAttendance({ employee_id: f.employee_id, entry_date: f.entry_date, hours: Number(f.hours), hours_kind: f.hours_kind })
      invalidate(); toast({ title: 'Hours logged' })
    }}>
      <EmployeeSelect value={f.employee_id} onChange={(v) => setF({ ...f, employee_id: v })} />
      <div className="grid gap-3 sm:grid-cols-3">
        <FormField label="Date" type="date" value={f.entry_date} onChange={(e) => setF({ ...f, entry_date: e.target.value })} />
        <FormField label="Hours" inputMode="decimal" value={f.hours} onChange={(e) => setF({ ...f, hours: e.target.value.replace(/[^\d.]/g, '') })} />
        <FormField label="Kind" control={
          <select value={f.hours_kind} onChange={(e) => setF({ ...f, hours_kind: e.target.value })}
            className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm">
            {HOURS_KINDS.map((k) => <option key={k} value={k}>{k.replace(/_/g, ' ')}</option>)}
          </select>
        } />
      </div>
    </CreateModal>
  )
}

export function LeaveRequestModal({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const invalidate = useInvalidate()
  const { toast } = useToast()
  const { data: types } = useLeaveTypes()
  const [f, setF] = useState({ employee_id: '', leave_type_id: '', start_date: '', end_date: '', hours_requested: '', reason: '' })
  return (
    <CreateModal open={open} onOpenChange={onOpenChange} title="Request leave" submitLabel="Submit request" onSubmit={async () => {
      if (!f.employee_id || !f.leave_type_id || !f.start_date || !f.end_date || !f.hours_requested) {
        toast({ title: 'All fields except reason are required', variant: 'destructive' }); throw new Error('validation')
      }
      await createLeaveRequest({ ...f, hours_requested: Number(f.hours_requested) })
      invalidate(); toast({ title: 'Leave requested' })
    }}>
      <EmployeeSelect value={f.employee_id} onChange={(v) => setF({ ...f, employee_id: v })} />
      <FormField label="Leave type" required control={
        <select value={f.leave_type_id} onChange={(e) => setF({ ...f, leave_type_id: e.target.value })}
          className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm">
          <option value="">Select…</option>
          {(types?.data ?? []).map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
        </select>
      } />
      <div className="grid gap-3 sm:grid-cols-3">
        <FormField label="Start" type="date" value={f.start_date} onChange={(e) => setF({ ...f, start_date: e.target.value })} />
        <FormField label="End" type="date" value={f.end_date} onChange={(e) => setF({ ...f, end_date: e.target.value })} />
        <FormField label="Hours" inputMode="decimal" value={f.hours_requested} onChange={(e) => setF({ ...f, hours_requested: e.target.value.replace(/[^\d.]/g, '') })} />
      </div>
      <FormField label="Reason" value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} />
    </CreateModal>
  )
}

export function ShiftModal({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const invalidate = useInvalidate()
  const { toast } = useToast()
  const [f, setF] = useState({ name: '', shift_kind: 'fixed', start_time: '09:00', end_time: '17:00', break_minutes: '30', shift_diff_pct: '0' })
  return (
    <CreateModal open={open} onOpenChange={onOpenChange} title="New shift" onSubmit={async () => {
      if (!f.name) { toast({ title: 'Name required', variant: 'destructive' }); throw new Error('validation') }
      await createShift({ ...f, break_minutes: Number(f.break_minutes) || 0, shift_diff_pct: Number(f.shift_diff_pct) || 0 })
      invalidate(); toast({ title: 'Shift created' })
    }}>
      <FormField label="Name" required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
      <div className="grid gap-3 sm:grid-cols-2">
        <FormField label="Start time" type="time" value={f.start_time} onChange={(e) => setF({ ...f, start_time: e.target.value })} />
        <FormField label="End time" type="time" value={f.end_time} onChange={(e) => setF({ ...f, end_time: e.target.value })} />
        <FormField label="Break minutes" inputMode="numeric" value={f.break_minutes} onChange={(e) => setF({ ...f, break_minutes: e.target.value.replace(/\D/g, '') })} />
        <FormField label="Shift differential %" inputMode="decimal" value={f.shift_diff_pct} onChange={(e) => setF({ ...f, shift_diff_pct: e.target.value.replace(/[^\d.]/g, '') })} />
      </div>
    </CreateModal>
  )
}

export function HolidayModal({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const invalidate = useInvalidate()
  const { toast } = useToast()
  const { data: calendars } = useHolidayCalendars()
  const [f, setF] = useState({ calendar_id: '', holiday_date: '', label: '', pay_multiplier: '1.5' })
  return (
    <CreateModal open={open} onOpenChange={onOpenChange} title="Add holiday" onSubmit={async () => {
      if (!f.calendar_id || !f.holiday_date || !f.label) { toast({ title: 'Calendar, date, and name required', variant: 'destructive' }); throw new Error('validation') }
      await createHoliday({ ...f, pay_multiplier: Number(f.pay_multiplier) || 1 })
      invalidate(); toast({ title: 'Holiday added' })
    }}>
      <FormField label="Calendar" required control={
        <select value={f.calendar_id} onChange={(e) => setF({ ...f, calendar_id: e.target.value })}
          className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm">
          <option value="">Select…</option>
          {(calendars?.data ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      } />
      <div className="grid gap-3 sm:grid-cols-3">
        <FormField label="Date" type="date" value={f.holiday_date} onChange={(e) => setF({ ...f, holiday_date: e.target.value })} />
        <FormField label="Name" value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} />
        <FormField label="Pay multiplier" inputMode="decimal" value={f.pay_multiplier} onChange={(e) => setF({ ...f, pay_multiplier: e.target.value.replace(/[^\d.]/g, '') })} />
      </div>
      {(calendars?.data ?? []).length === 0 ? (
        <p className="text-xs text-muted-foreground">No holiday calendars yet - create one first via the API or seed.</p>
      ) : null}
    </CreateModal>
  )
}

const EQUITY_TYPES = ['RSU', 'ISO', 'NSO', 'ESPP', 'RSA', 'PSU', 'SAR', 'PHANTOM']

export function EquityModal({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const invalidate = useInvalidate()
  const { toast } = useToast()
  const [f, setF] = useState({ employee_id: '', grant_number: '', grant_type: 'RSU', shares: '', grant_date: new Date().toISOString().slice(0, 10), cliff_months: '12', total_months: '48' })
  return (
    <CreateModal open={open} onOpenChange={onOpenChange} title="New equity grant" onSubmit={async () => {
      if (!f.employee_id || !f.grant_number || !f.shares) { toast({ title: 'Employee, grant #, and shares required', variant: 'destructive' }); throw new Error('validation') }
      await createEquityGrant({ ...f, shares: Number(f.shares), cliff_months: Number(f.cliff_months) || null, total_months: Number(f.total_months) || null })
      invalidate(); toast({ title: 'Equity grant created' })
    }}>
      <EmployeeSelect value={f.employee_id} onChange={(v) => setF({ ...f, employee_id: v })} />
      <div className="grid gap-3 sm:grid-cols-2">
        <FormField label="Grant #" value={f.grant_number} onChange={(e) => setF({ ...f, grant_number: e.target.value })} />
        <FormField label="Type" control={
          <select value={f.grant_type} onChange={(e) => setF({ ...f, grant_type: e.target.value })}
            className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm">
            {EQUITY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        } />
        <FormField label="Shares" inputMode="decimal" value={f.shares} onChange={(e) => setF({ ...f, shares: e.target.value.replace(/[^\d.]/g, '') })} />
        <FormField label="Grant date" type="date" value={f.grant_date} onChange={(e) => setF({ ...f, grant_date: e.target.value })} />
        <FormField label="Cliff months" inputMode="numeric" value={f.cliff_months} onChange={(e) => setF({ ...f, cliff_months: e.target.value.replace(/\D/g, '') })} />
        <FormField label="Total months" inputMode="numeric" value={f.total_months} onChange={(e) => setF({ ...f, total_months: e.target.value.replace(/\D/g, '') })} />
      </div>
    </CreateModal>
  )
}

const COVERAGE_TIERS = ['employee_only', 'employee_spouse', 'employee_children', 'family', 'waived']

export function EnrollmentModal({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const invalidate = useInvalidate()
  const { toast } = useToast()
  const { data: plans } = useBenefitPlans()
  const [f, setF] = useState({ employee_id: '', benefit_plan_id: '', coverage_tier: 'employee_only', employee_amount: '', employer_amount: '' })
  return (
    <CreateModal open={open} onOpenChange={onOpenChange} title="Enroll in benefit" onSubmit={async () => {
      if (!f.employee_id || !f.benefit_plan_id) { toast({ title: 'Employee and plan required', variant: 'destructive' }); throw new Error('validation') }
      await createBenefitEnrollment({
        employee_id: f.employee_id, benefit_plan_id: f.benefit_plan_id, coverage_tier: f.coverage_tier,
        employee_amount_minor_units: f.employee_amount ? Math.round(Number(f.employee_amount) * 100) : 0,
        employer_amount_minor_units: f.employer_amount ? Math.round(Number(f.employer_amount) * 100) : 0,
      })
      invalidate(); toast({ title: 'Enrollment created' })
    }}>
      <EmployeeSelect value={f.employee_id} onChange={(v) => setF({ ...f, employee_id: v })} />
      <FormField label="Plan" required control={
        <select value={f.benefit_plan_id} onChange={(e) => setF({ ...f, benefit_plan_id: e.target.value })}
          className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm">
          <option value="">Select…</option>
          {(plans?.data ?? []).map((p) => <option key={p.id} value={p.id}>{p.plan_name} ({p.plan_kind.replace(/_/g, ' ')})</option>)}
        </select>
      } />
      <div className="grid gap-3 sm:grid-cols-3">
        <FormField label="Coverage" control={
          <select value={f.coverage_tier} onChange={(e) => setF({ ...f, coverage_tier: e.target.value })}
            className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm">
            {COVERAGE_TIERS.map((t) => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
          </select>
        } />
        <FormField label="Employee / period" inputMode="decimal" value={f.employee_amount} onChange={(e) => setF({ ...f, employee_amount: e.target.value.replace(/[^\d.]/g, '') })} />
        <FormField label="Employer / period" inputMode="decimal" value={f.employer_amount} onChange={(e) => setF({ ...f, employer_amount: e.target.value.replace(/[^\d.]/g, '') })} />
      </div>
    </CreateModal>
  )
}

const RETIREMENT_KINDS = ['traditional_401k', 'roth_401k', 'simple_ira', '403b', '457b', 'solo_401k']

export function RetirementModal({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const invalidate = useInvalidate()
  const { toast } = useToast()
  const [f, setF] = useState({ employee_id: '', account_kind: 'traditional_401k', provider: '', catchup_eligible: false, annual_limit: '', per_period: '', election_pct: '' })
  return (
    <CreateModal open={open} onOpenChange={onOpenChange} title="Add retirement account" onSubmit={async () => {
      if (!f.employee_id) { toast({ title: 'Employee required', variant: 'destructive' }); throw new Error('validation') }
      await createRetirementAccount({
        employee_id: f.employee_id, account_kind: f.account_kind, provider: f.provider || null,
        catchup_eligible: f.catchup_eligible,
        annual_limit_minor_units: f.annual_limit ? Math.round(Number(f.annual_limit) * 100) : null,
        election_amount_minor_units: f.per_period ? Math.round(Number(f.per_period) * 100) : null,
        election_pct: f.election_pct ? Number(f.election_pct) : null,
      })
      invalidate(); toast({ title: 'Retirement account added' })
    }}>
      <EmployeeSelect value={f.employee_id} onChange={(v) => setF({ ...f, employee_id: v })} />
      <div className="grid gap-3 sm:grid-cols-2">
        <FormField label="Account kind" control={
          <select value={f.account_kind} onChange={(e) => setF({ ...f, account_kind: e.target.value })}
            className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm">
            {RETIREMENT_KINDS.map((k) => <option key={k} value={k}>{k.replace(/_/g, ' ')}</option>)}
          </select>
        } />
        <FormField label="Provider" value={f.provider} onChange={(e) => setF({ ...f, provider: e.target.value })} />
      </div>
      <div className="grid gap-3 sm:grid-cols-3">
        <FormField label="Annual limit" inputMode="decimal" value={f.annual_limit} onChange={(e) => setF({ ...f, annual_limit: e.target.value.replace(/[^\d.]/g, '') })} />
        <FormField label="Per-period contribution" inputMode="decimal" value={f.per_period} onChange={(e) => setF({ ...f, per_period: e.target.value.replace(/[^\d.]/g, '') })} />
        <FormField label="Or % of gross" inputMode="decimal" value={f.election_pct} onChange={(e) => setF({ ...f, election_pct: e.target.value.replace(/[^\d.]/g, '') })} />
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={f.catchup_eligible} onChange={(e) => setF({ ...f, catchup_eligible: e.target.checked })} />
        Catch-up eligible (age 50+)
      </label>
    </CreateModal>
  )
}

const GARNISHMENT_KINDS = ['child_support', 'federal_tax_levy', 'state_tax_levy', 'student_loan', 'creditor', 'bankruptcy', 'wage_assignment']

export function GarnishmentModal({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const invalidate = useInvalidate()
  const { toast } = useToast()
  const [f, setF] = useState({ employee_id: '', kind: 'child_support', order_number: '', remit_to_name: '', per_period: '', per_period_pct: '', balance: '' })
  return (
    <CreateModal open={open} onOpenChange={onOpenChange} title="Add garnishment order" onSubmit={async () => {
      if (!f.employee_id) { toast({ title: 'Employee required', variant: 'destructive' }); throw new Error('validation') }
      await createGarnishment({
        employee_id: f.employee_id, kind: f.kind, order_number: f.order_number || null,
        remit_to_name: f.remit_to_name || 'Remittance',
        per_period_amount_minor_units: f.per_period ? Math.round(Number(f.per_period) * 100) : null,
        per_period_pct: f.per_period_pct ? Number(f.per_period_pct) : null,
        balance_owed_minor_units: f.balance ? Math.round(Number(f.balance) * 100) : null,
      })
      invalidate(); toast({ title: 'Garnishment order added' })
    }}>
      <EmployeeSelect value={f.employee_id} onChange={(v) => setF({ ...f, employee_id: v })} />
      <div className="grid gap-3 sm:grid-cols-2">
        <FormField label="Type" control={
          <select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })}
            className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm">
            {GARNISHMENT_KINDS.map((k) => <option key={k} value={k}>{k.replace(/_/g, ' ')}</option>)}
          </select>
        } />
        <FormField label="Order number" value={f.order_number} onChange={(e) => setF({ ...f, order_number: e.target.value })} />
      </div>
      <FormField label="Remit to" value={f.remit_to_name} onChange={(e) => setF({ ...f, remit_to_name: e.target.value })} />
      <div className="grid gap-3 sm:grid-cols-3">
        <FormField label="Per-period amount" inputMode="decimal" value={f.per_period} onChange={(e) => setF({ ...f, per_period: e.target.value.replace(/[^\d.]/g, '') })} />
        <FormField label="Or % of disposable" inputMode="decimal" value={f.per_period_pct} onChange={(e) => setF({ ...f, per_period_pct: e.target.value.replace(/[^\d.]/g, '') })} />
        <FormField label="Total balance owed" inputMode="decimal" value={f.balance} onChange={(e) => setF({ ...f, balance: e.target.value.replace(/[^\d.]/g, '') })} />
      </div>
    </CreateModal>
  )
}

const HSA_KINDS = ['hsa', 'health_fsa', 'dependent_care_fsa', 'limited_purpose_fsa', 'commuter']

export function HsaFsaModal({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const invalidate = useInvalidate()
  const { toast } = useToast()
  const [f, setF] = useState({ employee_id: '', account_kind: 'hsa', annual_election: '' })
  return (
    <CreateModal open={open} onOpenChange={onOpenChange} title="Add HSA / FSA account" onSubmit={async () => {
      if (!f.employee_id) { toast({ title: 'Employee required', variant: 'destructive' }); throw new Error('validation') }
      await createHsaFsaAccount({ employee_id: f.employee_id, account_kind: f.account_kind, annual_election_minor_units: f.annual_election ? Math.round(Number(f.annual_election) * 100) : 0 })
      invalidate(); toast({ title: 'Account added' })
    }}>
      <EmployeeSelect value={f.employee_id} onChange={(v) => setF({ ...f, employee_id: v })} />
      <div className="grid gap-3 sm:grid-cols-2">
        <FormField label="Account kind" control={
          <select value={f.account_kind} onChange={(e) => setF({ ...f, account_kind: e.target.value })}
            className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm">
            {HSA_KINDS.map((k) => <option key={k} value={k}>{k.replace(/_/g, ' ')}</option>)}
          </select>
        } />
        <FormField label="Annual election" inputMode="decimal" value={f.annual_election} onChange={(e) => setF({ ...f, annual_election: e.target.value.replace(/[^\d.]/g, '') })} />
      </div>
    </CreateModal>
  )
}
